import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ArrowLeft, Download, Lock, Loader2, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cho_admin_token";

const StatCard = ({ label, value }) => (
  <div className="border border-ink/10 bg-white p-6 rounded-lg" data-testid={`stat-${label.toLowerCase()}`}>
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-soft">{label}</p>
    <p className="font-serif text-5xl text-gold mt-3">{value}</p>
  </div>
);

function LoginGate({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed(data.token);
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Incorrect password" : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-evergreen flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-cream p-8 md:p-10 rounded-lg shadow-2xl">
        <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold flex items-center justify-center mb-6">
          <Lock size={20} className="text-gold" />
        </div>
        <h1 className="font-serif text-3xl text-ink">Admin access</h1>
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold mt-1 mb-8">Columbus Home Options</p>
        <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-3">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          data-testid="admin-password-input"
          className="w-full bg-white border border-ink/15 px-4 py-3.5 text-ink rounded-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="w-full mt-6 flex items-center justify-center gap-2 bg-evergreen text-cream font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-4 hover:bg-gold transition-colors rounded-sm disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : null}
          Enter Dashboard
        </button>
        <a href="/" className="block text-center mt-6 font-mono text-[11px] tracking-wider text-ink-soft hover:text-gold transition-colors">
          ← Back to site
        </a>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, urgent: 0, owners: 0 });
  const [loading, setLoading] = useState(true);

  const authHeader = (t) => ({ headers: { Authorization: `Bearer ${t || token}` } });

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const load = async (t) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, authHeader(t)),
        axios.get(`${API}/leads/stats`, authHeader(t)),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) { logout(); toast.error("Session expired — please log in again."); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `columbus-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  if (!token) return <LoginGate onAuthed={(t) => setToken(t)} />;

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="admin-page">
      <header className="border-b border-ink/10 sticky top-0 bg-cream/90 backdrop-blur-xl z-10">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl tracking-tight">Leads</span>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Columbus Home Options</span>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={exportCsv} data-testid="export-btn"
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-cream bg-evergreen px-4 py-2.5 hover:bg-gold transition-colors rounded-sm">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={() => load()} data-testid="refresh-btn"
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-ink-soft hover:text-gold transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-ink-soft hover:text-terracotta transition-colors">
              <LogOut size={14} /> Logout
            </button>
            <a href="/" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-ink-soft hover:text-gold transition-colors">
              <ArrowLeft size={14} /> Site
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Today" value={stats.today} />
          <StatCard label="Urgent" value={stats.urgent} />
          <StatCard label="Owners" value={stats.owners} />
        </div>

        <div className="border border-ink/10 overflow-x-auto rounded-lg bg-white">
          <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
            <thead>
              <tr className="border-b border-ink/10 text-left font-mono text-[10px] tracking-[0.2em] uppercase text-ink-soft bg-cream-deep">
                <th className="p-4">Date</th>
                <th className="p-4">Name</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Address</th>
                <th className="p-4">Reason</th>
                <th className="p-4">Timeline</th>
                <th className="p-4">Condition</th>
                <th className="p-4">Price</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && !loading && (
                <tr><td colSpan={8} className="p-10 text-center text-ink-soft font-mono text-xs">No leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-ink/5 hover:bg-cream transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4 font-mono text-xs text-ink-soft whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="p-4 text-ink font-medium">{l.name}</td>
                  <td className="p-4 text-ink-soft">
                    <div>{l.phone}</div>
                    {l.email && <div className="text-xs text-ink-soft/60">{l.email}</div>}
                  </td>
                  <td className="p-4 text-ink-soft max-w-[200px]">{l.property_address}</td>
                  <td className="p-4 text-ink-soft">{l.reason || "—"}</td>
                  <td className="p-4">
                    <span className={`font-mono text-[10px] tracking-wider uppercase px-2 py-1 border rounded-sm ${
                      ["ASAP", "Within 1-3 months"].includes(l.timeline) ? "border-gold text-gold bg-gold/5" : "border-ink/15 text-ink-soft"
                    }`}>{l.timeline || "—"}</span>
                  </td>
                  <td className="p-4 text-ink-soft">{l.condition || "—"}</td>
                  <td className="p-4 text-ink font-mono text-xs">{l.price || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
